import {useCallback, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {toast} from "sonner";
import {useAuth} from "../../../../hooks/useAuth";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {emptyApi as injectedRtkApi, useRedeemInvitationMutation} from "../../../../store/generated/api";
import {authServiceRtk} from "../../../../services/authService";
import {store} from "../../../../store";
import {Button} from "../../../basic/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "../../../basic/card";
import {Alert, AlertDescription} from "../../../basic/alert";

export function JoinView() {
    const {t} = useTranslationWithKeys();
    const {getLocalizedErrorMessage} = useErrorHandler();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {user} = useAuth();
    const [redeemInvitation] = useRedeemInvitationMutation();

    const [status, setStatus] = useState<'loading' | 'redeeming' | 'success' | 'error'>('loading');
    const [error, setError] = useState<any>(null);

    const invitationCode = searchParams.get('code');

    const handleRedeemInvitation = useCallback(async () => {
        if (!invitationCode) return;

        setStatus('redeeming');
        setError(null);

        try {
            await redeemInvitation({
                vwpRedeemInvitationRequest: {
                    invitationCode
                }
            }).unwrap();

            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_token_expiry');

            const refreshed = await authServiceRtk.refreshToken();

            if (refreshed) {
                store.dispatch(injectedRtkApi.util.invalidateTags(['Auth']));
                setStatus('success');
                toast.success(t('join.success'));

                setTimeout(() => {
                    const hasHigherRoles = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));
                    navigate(hasHigherRoles ? '/dashboard' : '/settings');
                }, 2000);
            } else {
                setStatus('error');
                const refreshError = {key: 'join.error'};
                setError(refreshError);
            }

        } catch (error: any) {
            setStatus('error');
            setError(error);
        }
    }, [invitationCode, redeemInvitation, t, user?.roles, navigate]);

    useEffect(() => {
        if (status !== 'loading') return;

        if (!invitationCode) {
            setStatus('error');
            const invalidError = {key: 'join.invalidInvitation'};
            setError(invalidError);
            return;
        }

        handleRedeemInvitation();
    }, [status, invitationCode, handleRedeemInvitation]);

    if (status === 'loading') {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-full max-w-md">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-destructive">{t('join.errorTitle')}</CardTitle>
                            <CardDescription>{t('join.errorDescription')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{getLocalizedErrorMessage(error)}</AlertDescription>
                                </Alert>
                            )}
                            <Button
                                onClick={() => navigate('/dashboard')}
                                className="w-full"
                                variant="outline"
                            >
                                {t('common.backToDashboard')}
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (status === 'redeeming') {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">{t('join.redeeming')}</p>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-full max-w-md">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-green-600">{t('join.successTitle')}</CardTitle>
                            <CardDescription>{t('join.successDescription')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button
                                onClick={() => {
                                    const hasHigherRoles = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));
                                    navigate(hasHigherRoles ? '/dashboard' : '/settings');
                                }}
                                className="w-full"
                            >
                                {t('join.goToTeam')}
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return null;
}